import { useEffect, useState } from "react";
import { Clock, Square, Minus, Loader2 } from "lucide-react";
import { useAuthStore } from "../../store/useAuthStore";
import { useActivityTaskStore } from "../../store/useActivityTaskStore";
import { fmtDuration, displayElapsed } from "../../lib/activityTime";

export default function FloatingActivityTimer() {
  const { authUser } = useAuthStore() as any;
  const { activities, activitiesUpdatedAt, pending, syncActivities, stopActivity } =
    useActivityTaskStore();
  const [collapsed, setCollapsed] = useState(false);
  const [, setTick] = useState(0);

  const enabled = Boolean(authUser) && authUser?.role !== "Client";

  // Poll ERP-reconciled state app-wide so the running task is always known.
  useEffect(() => {
    if (!enabled) return;
    syncActivities();
    const poll = setInterval(() => syncActivities(), 3000);
    const tick = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [enabled]);

  if (!enabled) return null;

  const running = activities.find((a) => a.runningSince);
  if (!running) return null;

  const cat = typeof running.category === "object" ? running.category : null;
  const elapsed = displayElapsed(running, activitiesUpdatedAt);
  const estSeconds = (running.estimatedMinutes || 0) * 60;
  const progress = estSeconds ? Math.min(100, (elapsed / estSeconds) * 100) : 0;
  const startLabel = running.runningSince
    ? new Date(running.runningSince).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  // Collapsed: small circular ring with the live time.
  if (collapsed) {
    const size = 60, stroke = 5, r = (size - stroke) / 2, c = 2 * Math.PI * r;
    return (
      <button
        onClick={() => setCollapsed(false)}
        title={running.title || "Current activity"}
        className="fixed bottom-6 right-6 z-50 bg-white rounded-full shadow-xl ring-1 ring-emerald-200 hover:scale-105 transition-transform"
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} className="-rotate-90 absolute inset-0">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#ecfdf5" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#10b981"
            strokeWidth={stroke} strokeDasharray={c}
            strokeDashoffset={c - (c * progress) / 100} strokeLinecap="round"
          />
        </svg>
        <span className="absolute inset-0 grid place-items-center">
          <span className="flex flex-col items-center leading-none">
            <Clock size={12} className="text-emerald-500 mb-0.5" />
            <span className="text-[9px] font-bold font-mono text-slate-700">
              {fmtDuration(elapsed).slice(3)}
            </span>
          </span>
        </span>
      </button>
    );
  }

  // Expanded card.
  return (
    <div className="fixed bottom-6 right-6 z-50 w-72 bg-white rounded-2xl shadow-2xl border-2 border-emerald-300/70 p-4">
      <div className="flex items-center justify-between mb-2.5">
        <span className="flex items-center gap-1.5 text-[13px] font-bold text-emerald-600">
          <Clock size={15} className="animate-pulse" /> Current Activity
        </span>
        <button
          onClick={() => setCollapsed(true)}
          title="Minimize"
          className="p-1 text-slate-400 hover:bg-slate-100 rounded-md transition-colors"
        >
          <Minus size={15} />
        </button>
      </div>

      <div className="flex items-center gap-2 mb-2">
        {cat && (
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white"
            style={{ background: cat.color || "#2462b2" }}
          >
            {cat.name}
          </span>
        )}
      </div>
      <h3 className="text-[15px] font-bold text-[#2462b2] truncate mb-2.5">
        {running.title || running.description || "Untitled activity"}
      </h3>

      <div className="space-y-1 text-[12px] border-t border-slate-100 pt-2.5">
        <div className="flex justify-between">
          <span className="text-slate-500">Start Time:</span>
          <span className="font-semibold text-slate-700">{startLabel}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Estimated:</span>
          <span className="font-semibold text-slate-700">
            {estSeconds ? fmtDuration(estSeconds) : "—"}
          </span>
        </div>
      </div>

      <div className="mt-3 mb-3">
        <p className="text-[9px] uppercase tracking-[0.15em] font-bold text-slate-400">
          Elapsed Time
        </p>
        <p className="text-2xl font-mono font-extrabold text-emerald-600 leading-tight">
          {fmtDuration(elapsed)}
        </p>
      </div>

      <button
        onClick={() => stopActivity(running._id)}
        disabled={pending[running._id]}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {pending[running._id] ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <Square size={14} fill="currentColor" />
        )}
        Stop Activity
      </button>
    </div>
  );
}
